"use client";

import { ComponentProps, PropsWithChildren } from "react";
import { Badge as RadixBadge, Slot } from "@radix-ui/themes";
import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";

interface Props {
  sharedClasses?: string;
  shopsiteClasses?: string;
  whitelabelClasses?: string;
}

export default function Badge({
  children,
  sharedClasses = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  color,
  ref,
  ...restProps
}: ComponentProps<typeof RadixBadge> & PropsWithChildren<Props>) {
  const isShopsite = useIsShopsite();

  const shopsiteVariantClasses =
    color === "red"
      ? "bg-red-100 text-red-800"
      : color === "green"
      ? "bg-green-100 text-green-800"
      : color === "pink"
      ? "bg-fuchsia-100 text-fuchsia-800"
      : color === "orange"
      ? "bg-orange-100 text-orange-800"
      : color === "sky"
      ? "bg-sky-100 text-sky-800"
      : color === "yellow"
      ? "bg-yellow-100 text-yellow-800"
      : color === "teal"
      ? "bg-teal-100 text-teal-800"
      : color === "cyan"
      ? "bg-cyan-100 text-cyan-800"
      : color === "gray"
      ? "bg-gray-100 text-gray-800"
      : color === "amber"
      ? "bg-amber-100 text-amber-800"
      : "bg-indigo-100 text-indigo-800";

  if (!isShopsite)
    return (
      <RadixBadge
        ref={ref}
        className={joinClassNames(sharedClasses, whitelabelClasses)}
        color={color}
        {...restProps}
      >
        {children}
      </RadixBadge>
    );

  return restProps.asChild ? (
    <Slot
      className={joinClassNames(
        "flex items-center gap-0.5 px-2 py-0.5 rounded-full text-sm",
        shopsiteVariantClasses,
        sharedClasses,
        shopsiteClasses
      )}
      ref={ref}
    >
      {children}
    </Slot>
  ) : (
    <div
      className={joinClassNames(
        "flex items-center gap-0.5 px-2 py-0.5 rounded-full text-sm",
        shopsiteVariantClasses,
        sharedClasses,
        shopsiteClasses
      )}
      ref={ref as React.Ref<HTMLDivElement>}
    >
      {children}
    </div>
  );
}
